/**
 * GPU particle shaders for weapon muzzle flash effect.
 * Analytical simulation in vertex shader — no CPU per-particle updates.
 * Particles recycle via modulo on age. Two types derived from seed:
 * - Sparks (60%): small, fast, bright white-yellow, short-lived
 * - Flame puffs (40%): larger, slower, orange-red, linger slightly
 *
 * Geometry: THREE.Points with aBirthTime + aSeed buffer attributes.
 * Particles shoot forward (-Z in ship local space) in a wide cone.
 * Additive blending + HDR multiplier for bloom pickup.
 */

/** Number of muzzle flash particles per weapon. */
export const MUZZLE_PARTICLE_COUNT = 24;

/** Max lifetime for birth time staggering (must match flameLifetime below). */
export const MUZZLE_MAX_LIFETIME = 0.08;

// ---- Vertex Shader ----

export const muzzleVertexShader = /* glsl */ `
attribute float aBirthTime;
attribute float aSeed;

uniform float uTime;
uniform float uIntensity;

varying float vNormAge;
varying float vSeed;
varying float vIsFlame;
varying float vAge;

void main() {
  // Particle type: sparks (aSeed < 0.6) vs flame puffs (aSeed >= 0.6)
  vIsFlame = step(0.6, aSeed);
  vSeed = aSeed;

  // Lifetimes: very brief — muzzle flash is a quick flicker
  float sparkLifetime = 0.05;
  float flameLifetime = 0.08;
  float lifetime = mix(sparkLifetime, flameLifetime, vIsFlame);

  // Age with modulo recycling
  vAge = mod(uTime - aBirthTime, lifetime);
  vNormAge = vAge / lifetime;

  // Initial speed: moderate forward burst
  float sparkSpeed = 20.0;
  float flameSpeed = 8.0;
  float speed = mix(sparkSpeed, flameSpeed, vIsFlame);

  // Hash seed for direction independence (decorrelate from type classification)
  float dirSeed = fract(aSeed * 7.1381);
  float dirSeed2 = fract(aSeed * 13.7549);

  // Tight forward cone
  float coneAngle = 0.35;
  float angle = (dirSeed - 0.5) * coneAngle;
  float spreadX = sin(angle);
  float spreadZ = cos(angle);

  // Slight vertical spread
  float vertAngle = (dirSeed2 - 0.5) * 0.15;
  float spreadY = sin(vertAngle);

  // Fast drag — particles stop quickly
  float sparkDrag = 25.0;
  float flameDrag = 15.0;
  float drag = mix(sparkDrag, flameDrag, vIsFlame);

  // Analytical position: dist = speed/drag * (1 - exp(-drag * age))
  float dist = speed / drag * (1.0 - exp(-drag * vAge));

  // Particles shoot forward (-Z in ship local space)
  vec3 localPos = vec3(
    spreadX * dist,
    spreadY * dist,
    -spreadZ * dist
  );

  vec4 mvPosition = modelViewMatrix * vec4(localPos, 1.0);

  // Point sizes — small, subtle
  float sparkSize = 2.5;
  float flameSize = 5.0;
  float baseSize = mix(sparkSize, flameSize, vIsFlame);

  // Shrink with age squared
  float ageShrink = 1.0 - vNormAge * vNormAge;

  // Perspective scaling
  float perspective = 50.0 / -mvPosition.z;

  gl_PointSize = baseSize * ageShrink * perspective * uIntensity;
  gl_Position = projectionMatrix * mvPosition;
}
`;

// ---- Fragment Shader ----

export const muzzleFragmentShader = /* glsl */ `
varying float vNormAge;
varying float vSeed;
varying float vIsFlame;
varying float vAge;

void main() {
  // Circle shape via gl_PointCoord
  vec2 coord = gl_PointCoord - 0.5;
  float r = length(coord) * 2.0;

  // Hard-edge sparks, soft-edge flame puffs
  float edgeSoftness = mix(0.15, 0.5, vIsFlame);
  float circle = 1.0 - smoothstep(1.0 - edgeSoftness, 1.0, r);

  if (circle < 0.01) discard;

  // Spark colors: white-hot -> yellow -> orange
  vec3 sparkHot = vec3(1.0, 0.95, 0.85);
  vec3 sparkMid = vec3(1.0, 0.7, 0.15);
  vec3 sparkCool = vec3(1.0, 0.35, 0.05);

  vec3 sparkColor = mix(sparkHot, sparkMid, smoothstep(0.0, 0.4, vNormAge));
  sparkColor = mix(sparkColor, sparkCool, smoothstep(0.4, 0.9, vNormAge));

  // Flame colors: yellow-orange -> deep orange -> red
  vec3 flameHot = vec3(1.0, 0.8, 0.2);
  vec3 flameMid = vec3(1.0, 0.4, 0.05);
  vec3 flameCool = vec3(0.7, 0.12, 0.01);

  vec3 flameColor = mix(flameHot, flameMid, smoothstep(0.0, 0.4, vNormAge));
  flameColor = mix(flameColor, flameCool, smoothstep(0.4, 0.9, vNormAge));

  vec3 color = mix(sparkColor, flameColor, vIsFlame);

  // Per-particle flicker
  float flicker = 0.8 + 0.2 * sin(vAge * 180.0 + vSeed * 100.0);

  // Quadratic alpha fade with age
  float alphaFade = (1.0 - vNormAge) * (1.0 - vNormAge);

  float alpha = circle * alphaFade * flicker;

  if (alpha < 0.01) discard;

  // HDR multiplier for bloom pickup — moderate glow
  float hdr = 3.5;

  gl_FragColor = vec4(color * hdr * flicker, alpha);
}
`;
